import { useEffect, useRef, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckerPanel } from "./CheckerPanel";
import { PolishPanel } from "./PolishPanel";
import { IllustrationPanel } from "./IllustrationPanel";
import { AudioPanel } from "./AudioPanel";

export function PoemEditor({ poem, forms, reload }) {
  const [p, setP] = useState(poem);
  const [dirty, setDirty] = useState(false);
  const [check, setCheck] = useState(null);
  const t = useRef(null);
  useEffect(() => { setP(poem); setDirty(false); }, [poem]);
  useEffect(() => {
    clearTimeout(t.current);
    t.current = setTimeout(() => api.post("/poetry/check", { text: p.body, form: p.form }).then((r) => setCheck(r.data)).catch(() => {}), 500);
    return () => clearTimeout(t.current);
  }, [p.body, p.form]);
  const set = (k, v) => { setP({ ...p, [k]: v }); setDirty(true); };
  const save = async (quiet) => { try { await api.put(`/poems/${p.id}`, { title: p.title, body: p.body, form: p.form, section: p.section, epigraph: p.epigraph || "" }); setDirty(false); if (!quiet) toast.success("Poem saved"); reload(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const form = forms.find((f) => f.key === p.form);
  const applyTemplate = () => { if (p.body.trim() && !window.confirm("Replace the poem with the form's template?")) return; set("body", form?.template || ""); };
  return (
    <div className="grid 2xl:grid-cols-[1fr_380px] gap-5" data-testid="poem-editor">
      <div className="space-y-3">
        <div className="grid sm:grid-cols-[1fr_200px_180px] gap-2">
          <Input value={p.title} onChange={(e) => set("title", e.target.value)} maxLength={200} className="h-10 font-display text-lg" data-testid="poem-title" />
          <select value={p.form} onChange={(e) => set("form", e.target.value)} className="h-10 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="poem-form">{forms.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}</select>
          <Input value={p.section || ""} onChange={(e) => set("section", e.target.value)} placeholder="Section (optional)" maxLength={120} className="h-10" data-testid="poem-section" />
        </div>
        {form && <p className="text-xs text-slate-400" data-testid="poem-form-about">{form.about} {form.template && <button onClick={applyTemplate} className="text-amber-300 underline ml-1" data-testid="poem-use-template">Use template</button>}</p>}
        <Input value={p.epigraph || ""} onChange={(e) => set("epigraph", e.target.value)} placeholder="Epigraph or dedication line (optional)" maxLength={600} className="h-9 text-sm italic" data-testid="poem-epigraph" />
        <textarea value={p.body} onChange={(e) => set("body", e.target.value)} spellCheck rows={Math.min(22, Math.max(8, p.body.split("\n").length + 2))} className="w-full rounded-md bg-black/30 border border-slate-700 px-4 py-3 font-serif text-[15px] leading-7 whitespace-pre focus:outline-none focus:border-amber-400" placeholder="Write your poem. Leave a blank line between stanzas; leading spaces become indents." data-testid="poem-body" />
        <div className="flex gap-2 items-center"><Button onClick={() => save()} disabled={!dirty} className="bg-amber-600 hover:bg-amber-500" data-testid="poem-save"><Save size={13} className="mr-1" />Save</Button>{dirty && <span className="text-xs text-amber-400">Unsaved changes</span>}</div>
        <PolishPanel poem={poem} dirty={dirty} save={save} reload={reload} />
      </div>
      <div className="space-y-4">
        <CheckerPanel a={check} />
        <AudioPanel poem={poem} reload={reload} />
        <IllustrationPanel poem={poem} reload={reload} />
      </div>
    </div>
  );
}
